import { useCallback } from 'react';
import cn from 'clsx';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useAutoCompleteStore } from '@pkgs/components/hooks/stores/useAutoCompleteStore';
import ArrowLeftIcon from '@pkgs/components/assets/icons/small/Arrow-2-Left.svg';
import { IconButton } from '@ev-tech/ui-core-components/IconButton';

import { AutoComplete, type AutoSuggestOption } from './AutoComplete';
import { type LocationFilterProps } from '../LocationFilter/MobileLocationFilter';

import styles from '../LocationFilter/MobileLocationFilter.module.css';
import locationFilterStyles from '../LocationFilter/LocationFilter.module.css';

export const MobileAutoComplete = ({
  handleSelection,
  initialLocation,
  locationFilterProps,
}: {
  handleSelection?: (selectedLocation: AutoSuggestOption) => void;
  initialLocation: string;
  locationFilterProps?: LocationFilterProps;
}) => {
  const { t, featureFlags } = useAppContext();
  const isMobileModalOpen = useAutoCompleteStore((state) => state.isMobileModalOpen);
  const setIsMobileModalOpen = useAutoCompleteStore((state) => state.setIsMobileModalOpen);
  const setInputText = useAutoCompleteStore((state) => state.setInputText);

  const handleBackButtonClick = useCallback(() => {
    setIsMobileModalOpen(false);
    setInputText(initialLocation);
  }, [setIsMobileModalOpen, setInputText, initialLocation]);

  return (
    <div
      className={cn(locationFilterStyles.locationFilterContainer, styles.mobileLocationFilterContainer)}
      data-ai-search-enabled={featureFlags.isAiSearchEnabled}
      data-location-filter-container
      data-open={isMobileModalOpen}
      data-testid="search-components_location-filter_mobile"
    >
      {isMobileModalOpen && !featureFlags.isAiSearchEnabled ? (
        <IconButton
          className={locationFilterStyles.backButton}
          shape="square"
          icon={<ArrowLeftIcon />}
          onClick={handleBackButtonClick}
          label={t('search.common.close')}
          variant="tertiary"
        />
      ) : null}
      <div
        className={cn(locationFilterStyles.autoCompleteContainer, styles.mobileAutoCompleteContainer)}
        data-ai-search-enabled={featureFlags.isAiSearchEnabled}
        data-open={isMobileModalOpen}
      >
        <AutoComplete onSelectionChange={handleSelection} initialLocation={initialLocation} {...locationFilterProps} />
      </div>
    </div>
  );
};
